import { useState } from 'react'
import { callTool } from '../lib/tools'
import useNode from '../models/useNode'
import { useOrgRole } from '../hooks/useOrgRole'
import { getUserId } from '../lib/workos'
import { controlsNode } from '../utilities/orgRole'
import TextFieldForm from './TextFieldForm'
import FormControlLabel from '@mui/material/FormControlLabel'
import Typography from '@mui/material/Typography'
import Switch from '@mui/material/Switch'
import Box from '@mui/material/Box'
import {
  grantArgs,
  heldRetiredObserve,
  labelsAfter,
  localMcpOn,
  localMcpReach,
  localMcpTargets,
} from '../utilities/localMcp'

// The reserved label namespace node_grant/node_revoke own (router
// tools/node_grant.go). Read here to SHOW the state; written only through the
// router tools — a direct label write is refused by both node_label and a DB
// trigger. The keys, and why the app sends role "manage", are in
// utilities/localMcp.ts.

// At least one `key=` pair, matching what the router's ParseTargetSelector
// accepts: it skips pairs with no `=` or an empty key and errors only when
// nothing at all parses.
const TARGETS_VALID = /[^\s=,][^=,]*=/

type Props = {
  node: NodeRecord
  networkNames: string[]
}

// A node's local MCP is OFF or ON, with no tiers (router docs/PERMISSIONS.md
// § 8, app#389). When on, it acts for the node's OWNER with the owner's
// CURRENT rights, and the router checks them again on every call.
//
// The one switch is also what runs the local MCP server at all: turning it on
// (node_grant) starts the server,
// revoking stops it. There is no on-device veto — the agent-side one existed
// v0.4.18-v0.4.24 only, so the grant is the single control source.
export default function NodeGrant({ node, networkNames }: Readonly<Props>) {
  // The role in the node's TENANT, `owner_org_id` (G-1, app#385): `node_grant` / `node_revoke` ask
  // that org, not the org of the node's network.
  const { role } = useOrgRole(node.ownerOrgId ?? null)
  const update = useNode(s => s.update)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  const on = localMcpOn(node.labels)
  const retiredObserve = heldRetiredObserve(node.labels)
  // Only the node's CONTROLLERS turn it on or off: its owner, or an Owner/Admin of its org
  // (PERMISSIONS.md § 4, § 8), the org being the node's tenant above. The router gates node_grant /
  // node_revoke the same way (router PR #1832). A Member who owns the node is a controller.
  const canManage = controlsNode(role, node.ownerUserId, getUserId())
  const targetsValue = localMcpTargets(node.labels)
  const reach = localMcpReach(node.networkIds.length, networkNames)

  // node_grant clears role.mcp-targets on EVERY write, so a grant re-sends the
  // current scope. Otherwise a change of the targets would silently widen the
  // reach from "peers labelled X" to every node in the network.
  const write = async (nextOn: boolean, nextTargets: string) => {
    setBusy(true)
    setError('')
    try {
      if (nextOn) await callTool('node_grant', grantArgs(node.id, nextTargets))
      else await callTool('node_revoke', { node_ids: [node.id] })
      // Optimistic: mirror what the router wrote. The realtime nodes UPDATE
      // confirms it.
      update({ id: node.id, labels: labelsAfter(node.labels, nextOn, nextTargets) })
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Box>
      <FormControlLabel
        // Two-line labels: without flex-start the switch centres against the
        // pair and floats between the title and its caption.
        sx={{ alignItems: 'flex-start', marginLeft: 0, marginRight: 0 }}
        slotProps={{ typography: { sx: { width: '100%' } } }}
        disabled={busy || !canManage}
        control={
          <Switch
            size="small"
            color="secondary"
            sx={{ marginRight: 1 }}
            checked={on}
            onChange={e => void write(e.target.checked, targetsValue)}
          />
        }
        label={
          <>
            <Typography variant="body2">Local MCP</Typography>
            <Typography variant="caption" component="div" sx={{ color: 'text.secondary' }}>
              {on ? 'On. ' : 'Off. '}
              When on, the local MCP acts with the node owner&apos;s current rights on {reach.text}
              {reach.code && <Typography variant="code">{reach.code}</Typography>},
              and as root only where the owner holds root. Anyone who can run commands on this node can
              use it.
            </Typography>
          </>
        }
      />

      {retiredObserve && (
        <Typography variant="caption" component="div" sx={{ color: 'text.secondary', marginTop: 1 }}>
          This node had the read-only &quot;observe&quot; access, which no longer exists. Its local
          MCP is off. Turn it on to give it its owner&apos;s rights.
        </Typography>
      )}

      {on && (
        <Box sx={{ marginTop: 2 }}>
          <TextFieldForm
            size="small"
            label="Limit to peers labelled"
            value={targetsValue}
            valid={TARGETS_VALID}
            disabled={busy || !canManage}
            submitOnBlur
            // Returned, not fire-and-forget: TextFieldForm awaits this to drive its
            // spinner. submitOnBlur fires on every blur, so unchanged is a no-op.
            onSubmit={value => (value === targetsValue ? undefined : write(true, value))}
            helperText="Label pairs, e.g. role=edge,site=irvine. Empty means every node in its networks that the owner may use."
          />
        </Box>
      )}

      {!canManage && (
        <Typography variant="caption" component="div" sx={{ color: 'text.secondary', marginTop: 1 }}>
          Only the owner of this node, or an owner or admin of its organization, can change this.
        </Typography>
      )}
      {error && (
        <Typography variant="caption" color="error" component="div" sx={{ marginTop: 1 }}>
          {error}
        </Typography>
      )}
    </Box>
  )
}
